import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Alert from "../../components/alert";
import {
  getUnderwritingUiEmbedUrl,
  GetUnderwritingUiEmbedUrlResponse,
} from "../../actions/underwriting-ui.action";
import { useLazyGetDebugPayloadQuery } from "../../actions/policy-simulation.action";
import { PolicySimulationDebugPayloadVersion } from "../../models/policySimulation";
import { RoutePaths } from "../../models";
import dispatch from "../../middleware";
import useScreenName from "../../hooks/useScreenName";
import { ReactComponent as Loader } from "../../images/loader.svg";
import {
  UNDERWRITING_UI_EMBED_PAGES,
  UNDERWRITING_UI_ROUTE_PATHS,
} from "./underwritingUi.constants";
import UnderwritingUiPageShell from "./UnderwritingUiPageShell";

const IFRAME_NAME = "uw-simulate-frame";

export default function PolicySimulateEmbedPage() {
  useScreenName("Underwriting UI");
  const storeDispatch = useDispatch();
  const [payloadText, setPayloadText] = useState("");
  const [embedUrl, setEmbedUrl] = useState("");
  const [showPayload, setShowPayload] = useState(true);
  const [isLoadingDebugPayload, setIsLoadingDebugPayload] = useState(false);
  const [mintError, setMintError] = useState("");
  const [isMinting, setIsMinting] = useState(false);
  const [runCount, setRunCount] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);
  const location = useLocation();
  const [triggerDebugPayload] = useLazyGetDebugPayloadQuery();
  const debugRunId = new URLSearchParams(location.search).get("runId");
  const backTo = debugRunId
    ? RoutePaths.CREDIT_DECISION_POLICY_SIMULATION_DETAIL.replace(
        ":runId",
        encodeURIComponent(debugRunId),
      )
    : UNDERWRITING_UI_ROUTE_PATHS.POLICIES;

  useEffect(() => {
    setMintError("");
    const params = new URLSearchParams(location.search);
    const runId = params.get("runId");
    const rowNo = Number(params.get("rowNo"));
    const requestedVersion = params.get("payloadVersion");
    const payloadVersion: PolicySimulationDebugPayloadVersion =
      requestedVersion === "original" ? "original" : "effective";
    if (!runId || !Number.isInteger(rowNo) || rowNo < 1) {
      setShowPayload(true);
      setIsLoadingDebugPayload(false);
      return;
    }

    let active = true;
    setShowPayload(false);
    setIsLoadingDebugPayload(true);
    triggerDebugPayload({ runId, rowNo, payloadVersion })
      .unwrap()
      .then((payload) => {
        if (!active) return;
        const serialized = JSON.stringify(payload, null, 2);
        setPayloadText(serialized);
        setIsLoadingDebugPayload(false);
        void handleRun(serialized);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setShowPayload(true);
        setIsLoadingDebugPayload(false);
        setMintError(
          error instanceof Error
            ? error.message
            : "Failed to load debug payload",
        );
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, triggerDebugPayload]);

  // Mint a fresh signed URL per Run. The URL is used ONLY as the form's
  // action; the iframe gets a fixed name and src="about:blank" so it never
  // GETs the POST-token URL.
  async function handleRun(payloadOverride = payloadText) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(payloadOverride);
    } catch {
      setMintError("Paste a valid JSON payload");
      return;
    }
    if (parsed === null || typeof parsed !== "object") {
      setMintError("Paste a valid JSON payload");
      return;
    }

    setMintError("");
    setIsMinting(true);
    try {
      const response = (await dispatch(
        storeDispatch,
        getUnderwritingUiEmbedUrl({
          page: UNDERWRITING_UI_EMBED_PAGES.POLICY_SIMULATE,
        }),
      )) as GetUnderwritingUiEmbedUrlResponse | undefined;
      if (!response?.url) {
        throw new Error("Failed to mint embed URL");
      }
      setEmbedUrl(response.url);
      setRunCount((count) => count + 1);
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to mint embed URL";
      setMintError(message);
    } finally {
      setIsMinting(false);
    }
  }


  // Submit the hidden form only after the named iframe is mounted (render and
  // submit happen in the same commit). Re-running mints a fresh URL, which
  // re-triggers this effect and resubmits.
  useEffect(() => {
    if (runCount > 0 && embedUrl && formRef.current) {
      formRef.current.submit();
    }
  }, [runCount, embedUrl]);

  return (
    <UnderwritingUiPageShell
      title="Policy simulation"
      backTo={backTo}
      backLabel={debugRunId ? "Run detail" : "Policies"}
    >
      <div style={{ padding: "12px 16px" }}>
        {isLoadingDebugPayload ? (
          <div className="d-flex align-items-center">
            <Loader className="mr-2" />
            <span>Loading debug payload…</span>
          </div>
        ) : (
          <div className="form-group">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label htmlFor="simulation-payload" className="mb-0">
                JSON payload (policy name included in the payload)
              </label>
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={() => setShowPayload((visible) => !visible)}
              >
                {showPayload ? "Collapse payload" : "Show payload"}
              </button>
            </div>
            {showPayload && (
              <textarea
                id="simulation-payload"
                className="form-control"
                rows={8}
                spellCheck={false}
                value={payloadText}
                onChange={(event) => setPayloadText(event.target.value)}
              />
            )}
          </div>
        )}
        {mintError && <Alert type="danger" message={mintError} />}
        {!isLoadingDebugPayload && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleRun()}
            disabled={isMinting}
          >
            {isMinting ? "Minting signed URL…" : "Run simulation"}
          </button>
        )}
        {runCount > 0 && (
          <small className="text-muted" style={{ marginLeft: 8 }}>
            Run simulation…
          </small>
        )}
      </div>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <iframe
          name={IFRAME_NAME}
          title="Policy simulation"
          src="about:blank"
          sandbox="allow-same-origin allow-scripts allow-forms"
          allow="clipboard-write"
          referrerPolicy="no-referrer"
          style={{
            flex: 1,
            width: "100%",
            minHeight: 600,
            border: 0,
            display: "block",
          }}
        />
        <form
          ref={formRef}
          method="post"
          target={IFRAME_NAME}
          action={embedUrl || undefined}
          style={{ display: "none" }}
        >
          <input type="hidden" name="payload" value={payloadText} />
        </form>
      </div>
    </UnderwritingUiPageShell>
  );
}
